import { Ray, Vec3 } from 'playcanvas';

import { Camera } from './camera';
import type { SceneCollision } from './scene-collision-runtime';

const fromWorldPoint = new Vec3();
const toWorldPoint = new Vec3();
const worldDiff = new Vec3();
const moveVec = new Vec3();
const walkPosition = new Vec3();
const walkRay = new Ray();
const walkPush = { x: 0, y: 0, z: 0 };
const WALK_EYE_HEIGHT = 1.45;
const WALK_CAPSULE_HEIGHT = 1.5;
const WALK_CAPSULE_RADIUS = 0.22;
const WALK_GROUND_SNAP = 0.65;
const WALK_STEP_HEIGHT = 0.35;
const WALK_TARGET_STOP_DISTANCE = 0.35;
const WALK_GRAVITY = 9.8;
const WALK_GROUND_PROBES = [
    [0, 0],
    [WALK_CAPSULE_RADIUS, 0],
    [-WALK_CAPSULE_RADIUS, 0],
    [0, WALK_CAPSULE_RADIUS],
    [0, -WALK_CAPSULE_RADIUS]
];

// calculate the distance between two 2d points
const dist = (x0: number, y0: number, x1: number, y1: number) => Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);

class PointerController {
    update: (deltaTime: number) => void;
    destroy: () => void;

    constructor(camera: Camera, target: HTMLElement) {

        // Orbit mode: rotate camera around the focal point
        const orbit = (dx: number, dy: number) => {
            const azim = camera.azim - dx * camera.scene.config.controls.orbitSensitivity;
            const elev = camera.elevation - dy * camera.scene.config.controls.orbitSensitivity;
            camera.setAzimElev(azim, elev);
        };

        const look = (dx: number, dy: number) => {
            camera.look(dx, dy);
        };

        const pan = (x: number, y: number, dx: number, dy: number) => {
            // For panning to work at any zoom level, we use screen point to world projection
            // to work out how far we need to pan the pivotEntity in world space
            const c = camera.camera;
            const distance = camera.distanceTween.value.distance * camera.sceneRadius / camera.fovFactor;

            c.screenToWorld(x, y, distance, fromWorldPoint);
            c.screenToWorld(x - dx, y - dy, distance, toWorldPoint);

            worldDiff.sub2(toWorldPoint, fromWorldPoint);
            worldDiff.add(camera.focalPoint);

            camera.setFocalPoint(worldDiff);
        };

        const zoom = (amount: number) => {
            camera.setDistance(camera.distance - (camera.distance * 0.999 + 0.001) * amount * camera.scene.config.controls.zoomSensitivity, 2);
        };

        // mouse state
        let pressedButton = -1;  // no button pressed, otherwise 0, 1, or 2
        let x: number, y: number;

        // middle-mouse click-vs-drag tracking (for MMB single-click to focus)
        const CLICK_DRAG_THRESHOLD = 4;
        let mmbStartX = 0, mmbStartY = 0, mmbDragged = false;
        let lmbStartX = 0, lmbStartY = 0, lmbDragged = false;

        // walk mode runtime state
        let walkInitialized = false;
        let walkVelocityY = 0;
        let walkTarget: Vec3 | null = null;

        const getCollision = () => camera.scene.events.invoke('scene.collision.collider') as SceneCollision | null;

        const initializeWalk = () => {
            walkPosition.copy(camera.mainCamera.getLocalPosition());
            walkVelocityY = 0;
            walkTarget = null;
            walkInitialized = true;

            const collision = getCollision();
            if (collision) {
                const bottomY = walkPosition.y - WALK_EYE_HEIGHT;
                const ground = collision.queryRay(walkPosition.x, bottomY + WALK_GROUND_SNAP, walkPosition.z, 0, -1, 0, WALK_GROUND_SNAP + WALK_STEP_HEIGHT);
                if (ground) {
                    walkPosition.y = ground.y + WALK_EYE_HEIGHT;
                    camera.setFirstPersonPosition(walkPosition, 0);
                }
            }
        };

        const setWalkTargetFromPointer = (screenX: number, screenY: number) => {
            const collision = getCollision();
            if (!collision) return;
            camera.getRay(screenX, screenY, walkRay);
            const hit = collision.queryRay(
                walkRay.origin.x, walkRay.origin.y, walkRay.origin.z,
                walkRay.direction.x, walkRay.direction.y, walkRay.direction.z,
                camera.far
            );
            walkTarget = hit ? new Vec3(hit.x, hit.y + WALK_EYE_HEIGHT, hit.z) : null;
        };

        // touch state
        let touches: { id: number, x: number, y: number}[] = [];
        let midx: number, midy: number, midlen: number;

        const pointerdown = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') {
                // If a button is already pressed, ignore this press
                if (pressedButton !== -1) {
                    return;
                }
                target.setPointerCapture(event.pointerId);
                pressedButton = event.button;
                x = event.offsetX;
                y = event.offsetY;
                if (pressedButton === 1) {
                    mmbStartX = x;
                    mmbStartY = y;
                    mmbDragged = false;
                } else if (pressedButton === 0) {
                    lmbStartX = x;
                    lmbStartY = y;
                    lmbDragged = false;
                }
            } else if (event.pointerType === 'touch') {
                if (touches.length === 0) {
                    target.setPointerCapture(event.pointerId);
                }
                touches.push({
                    x: event.offsetX,
                    y: event.offsetY,
                    id: event.pointerId
                });

                if (touches.length === 2) {
                    midx = (touches[0].x + touches[1].x) * 0.5;
                    midy = (touches[0].y + touches[1].y) * 0.5;
                    midlen = dist(touches[0].x, touches[0].y, touches[1].x, touches[1].y);
                }
            }
        };

        const pointerup = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') {
                // Only release if this is the button that was initially pressed
                if (event.button === pressedButton) {
                    // MMB tap (no significant movement) -> focus on cursor point (orbit only; fly uses MMB for zoom)
                    if (pressedButton === 1 && camera.controlMode === 'orbit' && !mmbDragged) {
                        camera.pickFocalPoint(event.offsetX / target.clientWidth, event.offsetY / target.clientHeight);
                    }
                    if (pressedButton === 0 && camera.controlMode === 'walk' && !lmbDragged) {
                        setWalkTargetFromPointer(event.offsetX, event.offsetY);
                    }
                    pressedButton = -1;
                    target.releasePointerCapture(event.pointerId);
                }
            } else {
                touches = touches.filter(touch => touch.id !== event.pointerId);
                if (touches.length === 0) {
                    target.releasePointerCapture(event.pointerId);
                }
            }
        };

        const pointermove = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') {
                // Only process if we're tracking a button
                if (pressedButton === -1) {
                    return;
                }

                // Verify the button we're tracking is still pressed
                // 1 = left button, 4 = middle button, 2 = right button
                const buttonMask = [1, 4, 2][pressedButton];
                if ((event.buttons & buttonMask) === 0) {
                    // Button is no longer pressed, clean up
                    pressedButton = -1;
                    return;
                }

                const dx = event.offsetX - x;
                const dy = event.offsetY - y;
                x = event.offsetX;
                y = event.offsetY;

                if (pressedButton === 0 && !lmbDragged && dist(event.offsetX, event.offsetY, lmbStartX, lmbStartY) >= CLICK_DRAG_THRESHOLD) {
                    lmbDragged = true;
                }

                if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                    // First-person modes: left-drag to look around, middle to zoom, right works same as orbit
                    if (pressedButton === 0) {
                        if (camera.controlMode === 'walk' && lmbDragged) {
                            walkTarget = null;
                        }
                        look(dx, dy);
                    } else if (pressedButton === 1) {
                        zoom(dy * -0.02);
                    } else if (pressedButton === 2) {
                        // Right button: same behavior as orbit mode
                        const mod = event.shiftKey || event.ctrlKey ? 'look' :
                            (event.altKey || event.metaKey ? 'zoom' : 'pan');

                        if (mod === 'look') {
                            look(dx, dy);
                        } else if (mod === 'zoom') {
                            zoom(dy * -0.02);
                        } else {
                            pan(x, y, dx, dy);
                        }
                    }
                } else {
                    // Orbit mode:
                    // - left button: orbit
                    // - middle button (Blender-style): orbit, Shift -> pan, Ctrl -> zoom
                    //   (gated on a small drag threshold so a tap can be used to focus on release)
                    // - right button: pan, Shift/Ctrl -> orbit, Alt/Meta -> zoom
                    if (pressedButton === 1 && !mmbDragged) {
                        if (dist(event.offsetX, event.offsetY, mmbStartX, mmbStartY) < CLICK_DRAG_THRESHOLD) {
                            return;
                        }
                        mmbDragged = true;
                    }

                    let mod: 'orbit' | 'pan' | 'zoom';
                    if (pressedButton === 2) {
                        mod = event.shiftKey || event.ctrlKey ? 'orbit' :
                            (event.altKey || event.metaKey ? 'zoom' : 'pan');
                    } else if (pressedButton === 1) {
                        mod = event.shiftKey ? 'pan' :
                            (event.ctrlKey ? 'zoom' : 'orbit');
                    } else {
                        mod = 'orbit';
                    }

                    if (mod === 'orbit') {
                        orbit(dx, dy);
                    } else if (mod === 'zoom') {
                        zoom(dy * -0.02);
                    } else {
                        pan(x, y, dx, dy);
                    }
                }
            } else {
                if (touches.length === 1) {
                    const touch = touches[0];
                    const dx = event.offsetX - touch.x;
                    const dy = event.offsetY - touch.y;
                    touch.x = event.offsetX;
                    touch.y = event.offsetY;

                    if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                        look(dx, dy);
                    } else {
                        orbit(dx, dy);
                    }
                } else if (touches.length === 2) {
                    const touch = touches[touches.map(t => t.id).indexOf(event.pointerId)];
                    touch.x = event.offsetX;
                    touch.y = event.offsetY;

                    const mx = (touches[0].x + touches[1].x) * 0.5;
                    const my = (touches[0].y + touches[1].y) * 0.5;
                    const ml = dist(touches[0].x, touches[0].y, touches[1].x, touches[1].y);

                    if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                        // In fly mode, pinch moves forward/backward by moving focal point
                        const zoomDelta = (ml - midlen) * 0.01;
                        const worldTransform = camera.mainCamera.getWorldTransform();
                        const zAxis = worldTransform.getZ();
                        moveVec.copy(zAxis).mulScalar(-zoomDelta * camera.flySpeed);
                        const p = camera.focalPoint.add(moveVec);
                        camera.setFocalPoint(p);
                    } else {
                        pan(mx, my, (mx - midx), (my - midy));
                        zoom((ml - midlen) * 0.01);
                    }

                    midx = mx;
                    midy = my;
                    midlen = ml;
                }
            }
        };

        // Distinguish a physical mouse wheel from a trackpad two-finger
        // scroll. A single wheel event is unreliable (Magic Mouse, hi-res
        // mice, and macOS Shift-remapping all confuse per-event heuristics),
        // so we classify on the first event of a burst and let the rest of
        // the burst inherit that label. A burst is a run of wheel events
        // separated by less than BURST_GAP_MS - trackpads stream at ~60Hz
        // (~16ms), wheels emit one event per notch (typically >>50ms apart).
        const BURST_GAP_MS = 80;
        let lastWheelTime = -Infinity;
        let burstIsWheel = false;

        const classifyWheel = (event: WheelEvent) => {
            // Firefox: physical wheels report line/page mode; trackpads report
            // pixel mode. Firefox doesn't expose wheelDelta* so this is the
            // only reliable signal on Firefox.
            if (event.deltaMode !== WheelEvent.DOM_DELTA_PIXEL) {
                return true;
            }
            // Chrome / Safari: the non-standard wheelDelta{X,Y} properties
            // preserve the raw wheel-tick value (always multiples of ±120 per
            // notch) regardless of macOS scroll smoothing applied to
            // delta{X,Y}. Trackpads and Magic Mouse emit arbitrary values
            // that are essentially never aligned to 120.
            const e = event as WheelEvent & { wheelDeltaX?: number, wheelDeltaY?: number };
            if (typeof e.wheelDeltaY === 'number' && e.wheelDeltaY !== 0) {
                return e.wheelDeltaY % 120 === 0;
            }
            if (typeof e.wheelDeltaX === 'number' && e.wheelDeltaX !== 0) {
                return e.wheelDeltaX % 120 === 0;
            }
            // Last-resort fallback for browsers without wheelDelta*.
            const { deltaX, deltaY } = event;
            if (deltaX !== 0 && deltaY !== 0) {
                return false;
            }
            return Number.isInteger(deltaX) && Number.isInteger(deltaY);
        };

        const wheel = (event: WheelEvent) => {
            const now = performance.now();
            if (now - lastWheelTime > BURST_GAP_MS) {
                burstIsWheel = classifyWheel(event);
            }
            lastWheelTime = now;

            const { deltaX, deltaY } = event;

            // Some browsers (notably Safari/Firefox on macOS) remap a vertical
            // mouse wheel to deltaX when Shift is held. Only fall back to
            // deltaX for that remapped case so horizontal-only scrolling
            // (tilt wheel, horizontal trackpad swipe) is not treated as
            // zoom / fly movement.
            const wheelDelta = event.shiftKey && deltaY === 0 ? deltaX : deltaY;

            if (camera.controlMode === 'walk') {
                event.preventDefault();
                return;
            }

            if (camera.controlMode === 'fly') {
                // Fly mode: wheel moves forward/backward by moving focal point
                const factor = camera.flySpeed * 0.01;
                const worldTransform = camera.mainCamera.getWorldTransform();
                const zAxis = worldTransform.getZ();
                moveVec.copy(zAxis).mulScalar(wheelDelta * factor);
                const p = camera.focalPoint.add(moveVec);
                camera.setFocalPoint(p);
            } else if (burstIsWheel) {
                zoom(wheelDelta * -0.002);
            } else if (event.ctrlKey || event.metaKey) {
                zoom(deltaY * -0.02);
            } else if (event.shiftKey) {
                pan(event.offsetX, event.offsetY, deltaX, deltaY);
            } else {
                orbit(deltaX, deltaY);
            }

            event.preventDefault();
        };

        // FIXME: safari sends canvas as target of dblclick event but chrome sends the target element
        const canvas = camera.scene.app.graphicsDevice.canvas;

        const dblclick = (event: globalThis.MouseEvent) => {
            if (event.target === target || event.target === canvas) {
                // Switch to orbit mode when double-clicking to focus
                if (camera.controlMode === 'fly') {
                    camera.scene.events.fire('camera.setControlMode', 'orbit');
                }
                camera.pickFocalPoint(event.offsetX / target.clientWidth, event.offsetY / target.clientHeight);
            }
        };

        // fly movement state (updated via shortcut events)
        let flyForward = false;
        let flyBackward = false;
        let flyLeft = false;
        let flyRight = false;
        let flyDown = false;
        let flyUp = false;

        // track modifier keys for speed control (updated via shortcut events)
        let fastDown = false;
        let slowDown = false;

        // Clear all keys when window loses focus to prevent stuck keys
        const clearAllKeys = () => {
            flyForward = false;
            flyBackward = false;
            flyLeft = false;
            flyRight = false;
            flyDown = false;
            flyUp = false;
            fastDown = false;
            slowDown = false;
        };

        // Helper to switch to fly mode when a fly key is pressed
        const handleFlyKey = (down: boolean) => {
            if (down && camera.controlMode !== 'fly' && camera.controlMode !== 'walk') {
                camera.scene.events.fire('camera.setControlMode', 'fly');
            }
            if (down && camera.controlMode === 'walk') {
                walkTarget = null;
            }
        };

        // Listen for fly movement shortcut events
        const events = camera.scene.events;

        const onFlyForward = (down: boolean) => {
            flyForward = down;
            handleFlyKey(down);
        };
        const onFlyBackward = (down: boolean) => {
            flyBackward = down;
            handleFlyKey(down);
        };
        const onFlyLeft = (down: boolean) => {
            flyLeft = down;
            handleFlyKey(down);
        };
        const onFlyRight = (down: boolean) => {
            flyRight = down;
            handleFlyKey(down);
        };
        const onFlyDown = (down: boolean) => {
            flyDown = down;
            handleFlyKey(down);
        };
        const onFlyUp = (down: boolean) => {
            flyUp = down;
            handleFlyKey(down);
        };
        const onModifierFast = (down: boolean) => {
            fastDown = down;
        };
        const onModifierSlow = (down: boolean) => {
            slowDown = down;
        };
        const onControlMode = (mode: string) => {
            if (mode === 'walk') {
                initializeWalk();
            } else {
                walkInitialized = false;
                walkTarget = null;
            }
        };

        events.on('camera.fly.forward', onFlyForward);
        events.on('camera.fly.backward', onFlyBackward);
        events.on('camera.fly.left', onFlyLeft);
        events.on('camera.fly.right', onFlyRight);
        events.on('camera.fly.down', onFlyDown);
        events.on('camera.fly.up', onFlyUp);
        events.on('camera.modifier.fast', onModifierFast);
        events.on('camera.modifier.slow', onModifierSlow);
        events.on('camera.controlMode', onControlMode);

        const updateWalk = (deltaTime: number) => {
            const collision = getCollision();
            if (!collision) {
                camera.scene.events.fire('camera.setControlMode', 'fly');
                return;
            }
            if (!walkInitialized) {
                initializeWalk();
            }

            const forward = (flyForward ? 1 : 0) - (flyBackward ? 1 : 0);
            const strafe = (flyRight ? 1 : 0) - (flyLeft ? 1 : 0);
            const manualInput = !!(forward || strafe);

            const speedMod = fastDown ? 3 : (slowDown ? 0.35 : 1);
            const factor = deltaTime * camera.flySpeed * speedMod;
            const worldTransform = camera.worldTransform;

            moveVec.set(0, 0, 0);

            if (manualInput) {
                if (forward) {
                    const zAxis = worldTransform.getZ();
                    zAxis.y = 0;
                    if (zAxis.lengthSq() > 1e-8) zAxis.normalize();
                    moveVec.add(zAxis.mulScalar(-forward));
                }

                if (strafe) {
                    const xAxis = worldTransform.getX();
                    xAxis.y = 0;
                    if (xAxis.lengthSq() > 1e-8) xAxis.normalize();
                    moveVec.add(xAxis.mulScalar(strafe));
                }

                if (moveVec.lengthSq() > 1e-8) {
                    moveVec.normalize().mulScalar(factor);
                }
            } else if (walkTarget) {
                moveVec.sub2(walkTarget, walkPosition);
                moveVec.y = 0;
                const distance = moveVec.length();
                if (distance <= WALK_TARGET_STOP_DISTANCE) {
                    walkTarget = null;
                    moveVec.set(0, 0, 0);
                } else {
                    moveVec.mulScalar(Math.min(factor, distance) / distance);
                }
            }

            walkPosition.add(moveVec);

            let groundY = -Infinity;
            const bottomY = walkPosition.y - WALK_EYE_HEIGHT;
            const probeStartY = bottomY + WALK_STEP_HEIGHT;
            for (const [ox, oz] of WALK_GROUND_PROBES) {
                const hit = collision.queryRay(walkPosition.x + ox, probeStartY, walkPosition.z + oz, 0, -1, 0, WALK_STEP_HEIGHT + WALK_GROUND_SNAP);
                if (hit) {
                    groundY = Math.max(groundY, hit.y);
                }
            }

            if (groundY > -Infinity && bottomY - groundY <= WALK_STEP_HEIGHT) {
                walkPosition.y = groundY + WALK_EYE_HEIGHT;
                walkVelocityY = 0;
            } else {
                walkVelocityY -= WALK_GRAVITY * deltaTime;
                walkPosition.y += walkVelocityY * deltaTime;
            }

            const capsuleCenterY = walkPosition.y - WALK_EYE_HEIGHT + WALK_CAPSULE_HEIGHT * 0.5;
            const capsuleHalfHeight = Math.max(0, WALK_CAPSULE_HEIGHT * 0.5 - WALK_CAPSULE_RADIUS);
            if (collision.queryCapsule(walkPosition.x, capsuleCenterY, walkPosition.z, capsuleHalfHeight, WALK_CAPSULE_RADIUS, walkPush)) {
                walkPosition.x += walkPush.x;
                walkPosition.y += walkPush.y;
                walkPosition.z += walkPush.z;
                if (walkPush.y > 0) {
                    walkVelocityY = Math.max(0, walkVelocityY);
                }
            }

            camera.setFirstPersonPosition(walkPosition, 0);
        };

        this.update = (deltaTime: number) => {
            if (camera.controlMode === 'walk') {
                updateWalk(deltaTime);
                return;
            }

            if (camera.controlMode !== 'fly') return;

            // Fly mode: WASD for movement, Q/E for up/down - moves focal point
            const forward = (flyForward ? 1 : 0) - (flyBackward ? 1 : 0);
            const strafe = (flyRight ? 1 : 0) - (flyLeft ? 1 : 0);
            const vertical = (flyUp ? 1 : 0) - (flyDown ? 1 : 0);

            if (forward || strafe || vertical) {
                // Calculate speed modifier based on current modifier key state
                const speedMod = fastDown ? 10 : (slowDown ? 0.1 : 1);
                const factor = deltaTime * camera.flySpeed * speedMod;
                const worldTransform = camera.worldTransform;

                moveVec.set(0, 0, 0);

                // Forward/backward along horizontal forward direction (fixed Y)
                if (forward) {
                    const zAxis = worldTransform.getZ();
                    zAxis.y = 0;
                    zAxis.normalize();
                    moveVec.add(zAxis.mulScalar(-forward * factor));
                }

                // Strafe left/right (horizontal)
                if (strafe) {
                    const xAxis = worldTransform.getX();
                    xAxis.y = 0;
                    xAxis.normalize();
                    moveVec.add(xAxis.mulScalar(strafe * factor));
                }

                // Up/down in world space
                if (vertical) {
                    moveVec.y += vertical * factor;
                }

                // Move the focal point (camera follows due to orbit calculation)
                const p = camera.focalPoint.add(moveVec);
                camera.setFocalPoint(p);
            }
        };

        let destroy: () => void = null;

        const wrap = (target: any, name: string, fn: any, options?: any) => {
            const callback = (event: any) => {
                camera.scene.events.fire('camera.controller', name);
                fn(event);
            };
            target.addEventListener(name, callback, options);
            destroy = () => {
                destroy?.();
                target.removeEventListener(name, callback);
            };
        };

        wrap(target, 'pointerdown', pointerdown);
        wrap(target, 'pointerup', pointerup);
        wrap(target, 'pointermove', pointermove);
        wrap(target, 'wheel', wheel, { passive: false });
        wrap(target, 'dblclick', dblclick);
        wrap(window, 'blur', clearAllKeys);

        this.destroy = () => {
            destroy?.();
            events.off('camera.fly.forward', onFlyForward);
            events.off('camera.fly.backward', onFlyBackward);
            events.off('camera.fly.left', onFlyLeft);
            events.off('camera.fly.right', onFlyRight);
            events.off('camera.fly.down', onFlyDown);
            events.off('camera.fly.up', onFlyUp);
            events.off('camera.modifier.fast', onModifierFast);
            events.off('camera.modifier.slow', onModifierSlow);
            events.off('camera.controlMode', onControlMode);
        };
    }
}

export { PointerController };
